export const PDF_MARGIN_MM = 10;
export const PDF_BLOCK_GAP_MM = 5;
const FIT_EPS_PX = 2;
const HEADING_MAX_PX = 180;

export interface Segment {
  top: number;
  bottom: number;
}

export interface PackedSlice {
  top: number;
  bottom: number;
  pageBreakBefore: boolean;
  scale: number;
}

export interface TableMeasure {
  cardHeight: number;
  headTop: number;
  headBottom: number;
  rows: Segment[];
}

export interface TableChunk {
  pageBreakBefore: boolean;
  scale: number;
  cropTop: number;
  cropBottom: number;
  repeatHead: boolean;
  headTop: number;
  headBottom: number;
}

export const mergeVerticalRanges = (ranges: Segment[]): Segment[] => {
  const sorted = [...ranges].sort((left, right) => left.top - right.top || left.bottom - right.bottom);
  const merged: Segment[] = [];

  sorted.forEach((range) => {
    const last = merged[merged.length - 1];
    if (last && range.top <= last.bottom + 1) {
      last.bottom = Math.max(last.bottom, range.bottom);
      return;
    }
    merged.push({ ...range });
  });

  return merged;
};

export const toContiguousSegments = (ranges: Segment[], totalHeight: number): Segment[] => {
  if (totalHeight <= 1) return [];
  if (ranges.length === 0) return [{ top: 0, bottom: totalHeight }];

  const segments: Segment[] = [];
  let cursor = 0;

  ranges.forEach((range, index) => {
    const bottom = index === ranges.length - 1 ? totalHeight : range.bottom;
    if (bottom - cursor > 1) segments.push({ top: cursor, bottom });
    cursor = Math.max(cursor, range.bottom);
  });

  if (segments.length > 0 && cursor < totalHeight - 1) {
    segments[segments.length - 1].bottom = totalHeight;
  }

  return segments.length > 0 ? segments : [{ top: 0, bottom: totalHeight }];
};

const groupSlices = (packed: PackedSlice[]): PackedSlice[] => {
  const grouped: PackedSlice[] = [];

  packed.forEach((slice) => {
    const last = grouped[grouped.length - 1];
    const contiguous = last ? Math.abs(last.bottom - slice.top) < 2 : false;
    if (last && contiguous && !slice.pageBreakBefore && last.scale === 1 && slice.scale === 1) {
      last.bottom = slice.bottom;
      return;
    }
    grouped.push({ ...slice });
  });

  return grouped;
};

export const packAtomicSegments = (segments: Segment[], pageHeightPx: number, remainingPx: number): PackedSlice[] => {
  const items = segments.filter((segment) => segment.bottom - segment.top > 1);
  if (items.length === 0 || pageHeightPx <= 0) return [];

  const glued: Segment[] = [];
  for (let index = 0; index < items.length; index += 1) {
    const current = items[index];
    const next = items[index + 1];
    const height = current.bottom - current.top;
    if (next && height < HEADING_MAX_PX && next.bottom - current.top <= pageHeightPx + FIT_EPS_PX) {
      glued.push({ top: current.top, bottom: next.bottom });
      index += 1;
      continue;
    }
    glued.push(current);
  }

  const packed: PackedSlice[] = [];
  let space = remainingPx;

  glued.forEach((segment) => {
    const height = segment.bottom - segment.top;
    if (height > pageHeightPx + FIT_EPS_PX) {
      let top = segment.top;
      while (top < segment.bottom - 1) {
        const bottom = Math.min(segment.bottom, top + pageHeightPx);
        packed.push({
          top,
          bottom,
          pageBreakBefore: true,
          scale: 1,
        });
        top = bottom;
      }
      space = 0;
      return;
    }

    const pageBreakBefore = height > space + FIT_EPS_PX;
    if (pageBreakBefore) space = pageHeightPx;
    packed.push({
      top: segment.top,
      bottom: segment.bottom,
      pageBreakBefore,
      scale: 1,
    });
    space -= height;
  });

  return groupSlices(packed);
};

const wholeTableChunk = (measure: TableMeasure, pageBreakBefore: boolean, scale = 1): TableChunk => ({
  pageBreakBefore,
  scale,
  cropTop: 0,
  cropBottom: measure.cardHeight,
  repeatHead: false,
  headTop: measure.headTop,
  headBottom: measure.headBottom,
});

export const packTable = (measure: TableMeasure, pageHeightPx: number, remainingPx: number): TableChunk[] => {
  const { cardHeight, headTop, headBottom, rows } = measure;
  const headHeight = Math.max(0, headBottom - headTop);

  if (pageHeightPx <= 0) return [];

  if (rows.length === 0) {
    if (cardHeight <= pageHeightPx + FIT_EPS_PX) {
      return [wholeTableChunk(measure, cardHeight > remainingPx + FIT_EPS_PX)];
    }
    const slices: TableChunk[] = [];
    let top = 0;
    while (top < cardHeight - 1) {
      const bottom = Math.min(cardHeight, top + pageHeightPx);
      slices.push({
        pageBreakBefore: true,
        scale: 1,
        cropTop: top,
        cropBottom: bottom,
        repeatHead: false,
        headTop,
        headBottom,
      });
      top = bottom;
    }
    return slices;
  }

  if (cardHeight <= remainingPx + FIT_EPS_PX) return [wholeTableChunk(measure, false)];
  if (cardHeight <= pageHeightPx + FIT_EPS_PX) return [wholeTableChunk(measure, true)];

  const chunks: TableChunk[] = [];
  let cursor = 0;
  let space = remainingPx;

  const rowHeight = (index: number) => rows[index].bottom - rows[index].top;

  const fitRows = (available: number, firstChunk: boolean) => {
    const prefix = firstChunk ? rows[cursor].top : headHeight;
    let end = cursor;
    let used = prefix;

    while (end < rows.length) {
      const height = rowHeight(end);
      const tail = end === rows.length - 1 ? Math.max(0, cardHeight - rows[end].bottom) : 0;
      const overflowsStarted = used + height + tail > available + FIT_EPS_PX && end > cursor;
      const overflowsFirst = used + height > available + FIT_EPS_PX && end === cursor;
      if (overflowsStarted || overflowsFirst) break;
      used += height + tail;
      end += 1;
    }

    return end;
  };

  while (cursor < rows.length) {
    const firstChunk = chunks.length === 0;
    let pageBreakBefore = false;
    let end = fitRows(space, firstChunk);

    if (end === cursor) {
      pageBreakBefore = space < pageHeightPx - FIT_EPS_PX;
      space = pageHeightPx;
      end = fitRows(space, firstChunk);
    }

    if (end === cursor) {
      chunks.push({
        pageBreakBefore: true,
        scale: 1,
        cropTop: firstChunk ? 0 : rows[cursor].top,
        cropBottom: cursor === rows.length - 1 ? cardHeight : rows[cursor].bottom,
        repeatHead: !firstChunk,
        headTop,
        headBottom,
      });
      cursor += 1;
      space = 0;
      continue;
    }

    const isLast = end === rows.length;
    const cropTop = firstChunk ? 0 : rows[cursor].top;
    const cropBottom = isLast ? cardHeight : rows[end - 1].bottom;
    const drawn = firstChunk ? cropBottom - cropTop : headHeight + (cropBottom - cropTop);

    chunks.push({
      pageBreakBefore,
      scale: 1,
      cropTop,
      cropBottom,
      repeatHead: !firstChunk,
      headTop,
      headBottom,
    });

    space = Math.max(0, space - drawn);
    cursor = end;
  }

  return chunks;
};
